import {Box, Button, SvgIcon, Typography, useMediaQuery, useTheme} from '@mui/material';
import {IconUpvote} from '@zoom/icons-react';
import {useTranslation} from 'react-i18next';
import {useAppSelector} from 'Store/Store';

import {ConflictInfo, ConflictTag} from './ConflictInfo';

// @ts-ignore
import {ReactComponent as SunOutlined} from 'assets/svg/sun-outlined.svg';
import {toDateTime} from 'Components/BookingBlocksWidget/utils';
import {ZmTooltip} from 'Components/ZmTooltip/ZmTooltip';
import {renderTime} from 'Utils';

export type RenderSlotProps = {
  ariaLabel: string,
  appt: Appt,
  numVoters: number,
  id: string,
  bookedSlot?: {start: {dateTime: string, timeZone: string}, end: {dateTime: string, timeZone: string}},
  isBookedSlot: boolean,
  isSelected: boolean,
  isSlotUnavailable: boolean,
  onSelectSlot: (slot: Slot) => void,
  slot: Slot,
  isCrossTimeZone: boolean,
  hasTroubleShootingMode?: boolean,
  troubleShootingMode?: boolean,
  timeFormat: string,
  timeZone: string,
  voterEmail?: string,
};

export const DesktopSlot = (props: RenderSlotProps) => {
  const {
    appt,
    ariaLabel,
    hasTroubleShootingMode,
    id,
    isBookedSlot,
    isCrossTimeZone,
    isSelected,
    isSlotUnavailable,
    numVoters,
    onSelectSlot,
    slot,
    timeFormat,
    timeZone,
    troubleShootingMode,
    voterEmail,
  } = props;

  const theme = useTheme();
  const {t} = useTranslation();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const votesVisible = useAppSelector((s) => s.bookAppointments.appt?.votesVisible);
  const showOpenings = (appt?.capacity > 1 && appt?.showCapacity);

  return (
    <ZmTooltip
      arrow
      placement="bottom"
      title={
        (troubleShootingMode && slot?.conflicts && slot?.conflicts?.length > 0 ) ?
          <ConflictInfo appt={appt} spot={slot} /> :
          numVoters > 0 ?
            <div>
              {(slot.voters ?? [])
                .filter((email) => email !== voterEmail)
                .map((voter) => <div>{voter}</div>)}
            </div> :
            null
      }
      leaveDelay={200}
    >
      <div>
        <Button
          key={id}
          variant={
            isSelected ? 'contained' : 'outlined'
          }
          size={
            ((showOpenings && isBookedSlot)) ?
            'large' :
            (showOpenings || isBookedSlot || isSmallScreen ||
            (troubleShootingMode && slot?.conflicts && slot?.conflicts?.length > 0)) ?
            'medium' :
            'small'
          }
          color="inherit"
          sx={{
            fontWeight: '500',
            minWidth: '140px',
            marginBottom: '.5rem',
            whiteSpace: 'nowrap',
            height: 'unset',
            padding: '.3125rem 2rem', // 5px 32px
            ...troubleShootingMode && slot?.conflicts && slot?.conflicts?.length > 0 ? {
              'border': `1px dashed ${theme.palette.background.active} !important`,
            } : {
              'border': `1px solid ${theme.palette.background.active}`,
            },
            ...(isSelected ? {
              'backgroundColor': theme.palette.button.default,
              'color': theme.palette.common.white,
              '&:hover': {
                'backgroundColor': theme.palette.button.hover,
              },
              '&:active': {
                'backgroundColor': theme.palette.button.active,
              },
            } : {'color': theme.palette.button.default}),
            ...(votesVisible && {
              'justifyContent': 'flex-start',
            }),
            ...(votesVisible && numVoters > 0 && {
              'justifyContent': 'space-between',
            }),
            ...(isBookedSlot && {
              'padding': '20px 0',
            }),
          }}
          aria-label={ariaLabel}
          onClick={() => {
            if (!hasTroubleShootingMode) {
              onSelectSlot(slot);
            }
          }}
          // allow rescheduling to the original time slot if the original time is available
          disabled={isSlotUnavailable}
        >
          <div style={{
            'fontWeight': '500',
            'lineHeight': '1.25rem',
            'fontSize': '0.875rem',
            'color': 'inherit',
          }}>
            {renderTime(toDateTime(slot.start, timeZone), timeFormat)}
            {isCrossTimeZone &&
              <>
                &nbsp;
                <SvgIcon
                  fontSize="inherit"
                  component={SunOutlined}
                  inheritViewBox
                  sx={{marginRight: 1, fontSize: '1rem'}}
                />
              </>
            }
            {isBookedSlot && <>
              <br />
              <span style={{
                fontWeight: '400',
                lineHeight: '16px',
                fontSize: '0.75rem',
              }}>
                {t('rescheduling.originalBooking')}
              </span>
            </>}
            {(troubleShootingMode ?
              (slot?.conflicts?.length === 0 && showOpenings) : (showOpenings)) && <>
              <br />
              <span style={{
                fontWeight: '400',
                lineHeight: '1rem',
                fontSize: '0.75rem',
              }}>
                {t('common.seatsAvailable', {count: slot.availableNumber})}
              </span>
            </>}
            {(slot?.conflicts && slot?.conflicts?.length > 0) && troubleShootingMode && <>
              <br />
              <Typography gutterBottom color="error" fontWeight="600" fontSize=".75rem">
                {t('common.unavailable')}
              </Typography>
              <Box
                component="span"
                sx={{
                  fontWeight: '400',
                  lineHeight: '1rem',
                  fontSize: '0.75rem',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: '2px',
                  maxWidth: '150px',
                }}>
                {slot.conflicts.map((conflict) => {
                  return <ConflictTag key={conflict.code} conflict={conflict} theme={theme} />;
                })}
              </Box>
            </>}
          </div>
          {
            numVoters > 0 &&
              <div style={{display: 'flex', alignItems: 'center'}}>
                <IconUpvote sx={{margin: '0 4px'}}/>
                {` ${numVoters}`}
              </div>
          }
        </Button>
      </div>
    </ZmTooltip>
  );
};
